import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Clock } from 'lucide-react';
import AppIcon from '../../../../components/AppIcon';
import { StatusBadge } from '../../../../components/ui/StatusBadge';
import { Progress } from '../../../../components/ui/Progress';
import { Alert } from '../../../../components/ui/Alert';

interface ReflectionProgressProps {
  totalPrompts: number;
  completedPrompts: number;
  currentMonth: string;
}

const ReflectionProgress: React.FC<ReflectionProgressProps> = ({
  totalPrompts,
  completedPrompts,
  currentMonth,
}) => {
  const { t } = useTranslation();
  const progress = totalPrompts > 0 ? (completedPrompts / totalPrompts) * 100 : 0;
  const isComplete = completedPrompts === totalPrompts;

  return (
    <div className="bg-card border border-border rounded-xl p-6 mb-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold text-foreground">
          {t('performance.reflections.progressTitle')}
        </h3>
        <StatusBadge
          variant={isComplete ? 'success' : 'info'}
          icon={isComplete ? CheckCircle : Clock}
        >
          {isComplete
            ? t('performance.reflections.complete')
            : t('performance.reflections.inProgress')}
        </StatusBadge>
      </div>

      <p className="text-sm text-muted-foreground mb-3">
        {t('performance.reflections.promptsCompleted', {
          completed: completedPrompts,
          total: totalPrompts,
          month: currentMonth,
        })}
      </p>

      <Progress
        value={progress}
        variant={isComplete ? 'success' : 'default'}
        size="lg"
        className="mb-4"
      />

      <div className="flex justify-between">
        <span className="text-xs text-muted-foreground">
          {t('performance.reflections.percentComplete', { percent: Math.round(progress) })}
        </span>
        <span className="text-xs text-muted-foreground">
          {t('performance.reflections.remaining', { count: totalPrompts - completedPrompts })}
        </span>
      </div>

      {isComplete && (
        <Alert variant="success" className="mt-4">
          <p className="text-sm flex items-center gap-2">
            <AppIcon name="CheckCircle" size={16} className="shrink-0" />
            {t('performance.reflections.allSubmitted')}
          </p>
        </Alert>
      )}
    </div>
  );
};

export default ReflectionProgress;
